import React from 'react';
import { Column } from '@/types/common';
import {
  warehouseApi,
  warehouseExport,
  WarehouseSku,
} from '@/services/api/warehouse';
import {
  codeCls,
  ExportCsvButton,
  fmtQty,
  ListCard,
  metaCls,
  nameCls,
  numCls,
  PageHead,
  SKU_STATUS,
  StatusBadge,
  useListState,
  useLoad,
} from './shared';
import WarehouseFilter from './WarehouseFilter';
import { SKU_FILTERS } from './filterFields';

// The SKU master is per client, not per hub — so no Hub Name column here.
const COLUMNS: Column<WarehouseSku>[] = [
  {
    key: 'sku',
    title: 'SKU Code',
    render: (_v, s) => <span className={codeCls}>{s.sku}</span>,
  },
  {
    key: 'itemName',
    title: 'SKU Name',
    render: (_v, s) => (
      <div>
        <p className={nameCls}>{s.itemName}</p>
        {s.barcode && <p className={`${metaCls} font-mono`}>{s.barcode}</p>}
      </div>
    ),
  },
  {
    key: 'brand',
    title: 'Brand',
    render: (_v, s) => (
      <span className={s.brand ? nameCls : metaCls}>{s.brand || '—'}</span>
    ),
  },
  {
    key: 'category',
    title: 'Category / Type',
    render: (_v, s) =>
      s.productCategory || s.productType ? (
        <div>
          <p className={nameCls}>{s.productCategory ?? '—'}</p>
          {s.productType && <p className={metaCls}>{s.productType}</p>}
        </div>
      ) : (
        <span className={metaCls}>—</span>
      ),
  },
  {
    key: 'uomName',
    title: 'UOM',
    render: (_v, s) => <span className={metaCls}>{s.uomName}</span>,
  },
  {
    key: 'weightKg',
    title: 'Weight / PCS',
    align: 'right',
    render: (_v, s) => (
      <span className={numCls}>
        {s.weightKg ? `${fmtQty(s.weightKg)} kg` : '—'}
      </span>
    ),
  },
  {
    key: 'status',
    title: 'Status',
    render: (_v, s) => (
      <StatusBadge map={SKU_STATUS} value={s.status ?? 'ACTIVE'} />
    ),
  },
];

const SkuPage: React.FC = () => {
  const q = useListState('ALL');
  const search = q.search || undefined;
  const list = useLoad(
    () =>
      warehouseApi.listSkus(
        { search, page: q.page, limit: q.limit },
        q.filters
      ),
    [search, q.filterKey, q.page, q.limit]
  );

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <PageHead
        title="SKU"
        description="Daftar SKU Anda yang terdaftar di gudang Dash."
        actions={
          <ExportCsvButton
            what="SKU"
            onExport={() => warehouseExport.skus({ search }, q.filters)}
          />
        }
      />
      <ListCard<WarehouseSku, 'ALL'>
        searchPlaceholder="Cari SKU code, nama, brand, atau barcode…"
        onSearch={q.setSearch}
        filter={
          <WarehouseFilter
            title="SKU"
            fields={SKU_FILTERS}
            value={q.filters}
            onApply={q.setFilters}
          />
        }
        columns={COLUMNS}
        rows={list.data?.data ?? []}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        emptyTitle="Belum ada SKU"
        page={q.page}
        limit={q.limit}
        total={list.data?.pagination.total ?? 0}
        onPageChange={q.setPage}
        onLimitChange={q.setLimit}
      />
    </div>
  );
};

export default SkuPage;
